// src/components/Currency.jsx
import React, { useState, useEffect } from "react";
import "./Currency.css";
import { FaSearch } from "react-icons/fa";
import { useCurrency } from "../Context/CurrencyContext";

// Close SVG Icon Component - No border, clean X button
const CloseIcon = ({ onClick }) => (
  <button 
    className="close-icon" 
    onClick={onClick}
    aria-label="Close"
    type="button"
  >
    <svg 
      className="svg-icon svg-icon--close" 
      aria-hidden="true" 
      focusable="false" 
      role="presentation" 
      xmlns="http://www.w3.org/2000/svg" 
      width="24" 
      height="24" 
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="18" y1="6" x2="6" y2="18"></line>
      <line x1="6" y1="6" x2="18" y2="18"></line>
    </svg>
  </button>
);

const Currency = ({ open, onClose, onSelectCountry, propSelectedCountry }) => {
  const [search, setSearch] = useState("");
  const [filteredCountries, setFilteredCountries] = useState([]);
  
  const { 
    selectedCountry: contextSelectedCountry, 
    setSelectedCountry,
    countries,
    countriesLoading,
    refreshCountries 
  } = useCurrency();

  // Use context or prop
  const selectedCountry = propSelectedCountry || contextSelectedCountry;

  // ---------- HELPER: convert three‑letter codes to two‑letter ISO codes ----------
  const mapToTwoLetterCode = (code) => {
    if (!code) return "us";
    const upper = code.toUpperCase().trim();
    const threeToTwo = {
      "USA": "US", "IND": "IN", "GBR": "GB", "CAN": "CA", "AUS": "AU",
      "DEU": "DE", "FRA": "FR", "ITA": "IT", "ESP": "ES", "MEX": "MX",
      "JPN": "JP", "CHN": "CN", "BRA": "BR", "ZAF": "ZA", "RUS": "RU",
      "KOR": "KR", "SAU": "SA", "ARE": "AE", "NLD": "NL", "SWE": "SE",
      "NOR": "NO", "DNK": "DK", "FIN": "FI", "CHE": "CH", "BEL": "BE",
      "AUT": "AT", "POL": "PL", "CZE": "CZ", "HUN": "HU", "GRC": "GR",
      "PRT": "PT", "IRL": "IE", "NZL": "NZ", "SGP": "SG", "MYS": "MY",
      "THA": "TH", "VNM": "VN", "PHL": "PH", "IDN": "ID", "PAK": "PK",
      "BGD": "BD", "LKA": "LK", "NPL": "NP", "MMR": "MM", "IRN": "IR",
      "IRQ": "IQ", "ISR": "IL", "JOR": "JO", "LBN": "LB", "EGY": "EG",
      "MAR": "MA", "DZA": "DZ", "TUN": "TN", "LBY": "LY", "SDN": "SD",
      "ETH": "ET", "KEN": "KE", "NGA": "NG", "GHA": "GH", "CIV": "CI",
      "CMR": "CM", "ZWE": "ZW", "UGA": "UG", "TZA": "TZ", "AGO": "AO",
      "MOZ": "MZ", "NAM": "NA", "BWA": "BW", "ZMB": "ZM", "MWI": "MW",
      "MDG": "MG", "MUS": "MU", "SYC": "SC", "COM": "KM", "FJI": "FJ",
      "PNG": "PG", "SLB": "SB", "VUT": "VU", "WSM": "WS", "TON": "TO",
      "PLW": "PW", "FSM": "FM", "MHL": "MH", "KIR": "KI", "NRU": "NR",
      "TUV": "TV", "COK": "CK", "NIU": "NU", "GLP": "GP", "MTQ": "MQ",
      "GUF": "GF", "REU": "RE", "MYT": "YT", "SPM": "PM", "WLF": "WF",
      "PYF": "PF", "NCL": "NC"
    };
    if (upper.length === 2) return upper;
    return threeToTwo[upper] || "US";
  };

  // Safely get country code (whatever the API returns)
  const getCountryCode = (country) => {
    return country?.countryCode || country?.code || 'USD';
  };

  // ----- FIXED: use API flag if available, otherwise fallback to Shopify CDN -----
  const getCountryFlag = (country) => {
    // 1. Try to get flag directly from the API response
    if (country?.flag && typeof country.flag === 'string' && country.flag.trim() !== '') {
      return country.flag;
    }
    if (country?.flagUrl && typeof country.flagUrl === 'string' && country.flagUrl.trim() !== '') {
      return country.flagUrl;
    }
    if (country?.image && typeof country.image === 'string' && country.image.trim() !== '') {
      return country.image;
    }
    if (country?.countryFlag && typeof country.countryFlag === 'string' && country.countryFlag.trim() !== '') {
      return country.countryFlag;
    }
    // 2. Fallback to Shopify CDN using the mapped two‑letter code
    const rawCode = getCountryCode(country);
    const twoLetterCode = mapToTwoLetterCode(rawCode).toLowerCase();
    return `https://cdn.shopify.com/static/images/flags/${twoLetterCode}.svg`;
  };

  // ---------- Rest of your original component (unchanged) ----------
  useEffect(() => {
    if (countries && Array.isArray(countries) && countries.length > 0) {
      const filtered = countries.filter((item) => {
        if (!item || !item.countryName) return false;
        return item.countryName.toLowerCase().includes(search.toLowerCase());
      });
      setFilteredCountries(filtered);
    } else {
      setFilteredCountries([]);
    }
  }, [countries, search]);

  useEffect(() => {
    if (open && (!countries || countries.length === 0)) {
      refreshCountries();
    }
  }, [open, countries, refreshCountries]);

  const handleCountrySelect = (country) => {
    if (!country) return;
    
    setSelectedCountry(country);
    if (onSelectCountry) {
      onSelectCountry(country);
    }
    onClose();
  };

  const getCountryName = (country) => {
    return country?.countryName || country?.name || 'Unknown';
  };

  const getCurrencySymbol = (country) => {
    return country?.currency?.symbol || country?.symbol || '$';
  };

  const getCurrencyCode = (country) => {
    return country?.currency?.code || country?.code || 'USD';
  };

  return (
    // Added wrapper div for style isolation
    <div className="currency-root">
      {open && <div className="currency-overlay" onClick={onClose} />}

      <aside className={`currency-drawer ${open ? "open" : ""}`}>
        <div className="currency-header">
          <h2>Localization options</h2>
          <CloseIcon onClick={onClose} />
        </div>

        <div className="currency-tabs">
          <button className="active">Country/region</button>
        </div>

        {selectedCountry && (
          <div className="current-location">
            <img 
              src={getCountryFlag(selectedCountry)} 
              alt={getCountryName(selectedCountry)}
              onError={(e) => {
                e.target.src = "https://cdn.shopify.com/static/images/flags/us.svg";
              }}
            />
            <div className="location-info">
              <strong>
                {getCountryName(selectedCountry)} | {getCurrencyCode(selectedCountry)} {getCurrencySymbol(selectedCountry)}
              </strong>
              <p>
                You are currently shipping to {getCountryName(selectedCountry)} and your order will be billed in {getCurrencyCode(selectedCountry)} {getCurrencySymbol(selectedCountry)}.
                {selectedCountry?.taxPercentage > 0 && (
                  <span className="tax-info"> Tax: {selectedCountry.taxPercentage}%</span>
                )}
              </p>
            </div>
          </div>
        )}

        <div className="search-box">
          <FaSearch />
          <input
            type="text"
            placeholder="Search location..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="country-list">
          {countriesLoading ? (
            <div className="loading-spinner">
              <p>Loading countries...</p>
            </div>
          ) : filteredCountries.length > 0 ? (
            filteredCountries.map((item) => {
              if (!item) return null;
              
              const isSelected = selectedCountry?.id === item.id;
              const countryName = getCountryName(item);
              const currencySymbol = getCurrencySymbol(item);
              const currencyCode = getCurrencyCode(item);
              const flagUrl = getCountryFlag(item);
              
              return (
                <div 
                  className={`country-row ${isSelected ? 'selected' : ''}`}
                  key={item.id || item.countryCode}
                  onClick={() => handleCountrySelect(item)}
                >
                  <div className="left">
                    <img 
                      src={flagUrl} 
                      alt={countryName}
                      onError={(e) => {
                        e.target.src = "https://cdn.shopify.com/static/images/flags/us.svg";
                      }}
                    />
                    <div className="country-info">
                      <span className="country-name">{countryName}</span>
                    </div>
                  </div>
                  <div className="right">
                    <span className="currency-code">{currencyCode}</span>
                    <span className="currency-symbol">{currencySymbol}</span>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="no-results">
              <p>{search ? `No countries found matching "${search}"` : 'No countries available'}</p>
              {!countriesLoading && countries.length === 0 && (
                <button 
                  className="retry-button"
                  onClick={() => refreshCountries()}
                >
                  Retry
                </button>
              )}
            </div>
          )}
        </div>
      </aside>
    </div>
  );
};

export default Currency;